import { reportPlatformError } from "$root/api/error-reporting";
import { ScrollWrapper, Text } from "@mdotm/shadcn/legacy/components";
import { Skeleton } from "@mdotm/shadcn/ui";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HistoryBox } from "./boxes/HistoryBox";
import { groupHistory } from "./historyGrouping";
import { HistorySectionIcon } from "./icons/HistorySectionIcon";
import { SphereAIPanelLane } from "./SphereAIPanelLane";
import { sphereAiConversationsRepository } from "./repository";
import type { ConversationInfo } from "./serverChatStore";

/** How many Recent questions to show before the "Show all" affordance (a reservoir shows the head, not the archive). */
const RECENT_LIMIT = 12;

/**
 * Empty state for the History tab. Mirrors the Chat welcome's identity zone (illustration → title →
 * subtitle) minus suggestions and composer. The illustration sits directly on white, same as
 * Feedback — no tile behind it. The copy frames History as a reservoir of questions to ask again,
 * not an archive to re-read.
 */
function SphereAIHistoryEmpty(): React.JSX.Element {
	const { t } = useTranslation();
	return (
		<div className="flex w-full flex-col items-center px-4 pt-4 pb-6">
			<div className="flex flex-col items-center gap-2 text-center">
				<HistorySectionIcon className="h-14 w-auto" />
				<Text type="Body/L/Medium" color={themeCSSVars.palette_N700}>
					{t("sphereAi.history.emptyTitle")}
				</Text>
				<Text as="div" type="Body/M/Book" color={themeCSSVars.palette_N400} className="max-w-xs">
					{t("sphereAi.history.emptySubtitle")}
				</Text>
			</div>
		</div>
	);
}

/**
 * Placeholder list shown while the stored conversations are being read. It exists because the
 * empty state ("No questions yet") is a CLAIM: showing it before the read resolves tells the user
 * their history is gone. Same row rhythm as the real list, so nothing shifts when data lands.
 */
function SphereAIHistoryLoading(): React.JSX.Element {
	return (
		<div aria-busy="true" className="px-2 pt-4 pb-4">
			<Skeleton className="mb-2 ml-2 h-3 w-16" />
			{["w-full", "w-4/5", "w-11/12", "w-3/5"].map((width) => (
				<div key={width} className="px-2 py-2">
					<Skeleton className={`h-4 ${width}`} />
				</div>
			))}
		</div>
	);
}

/** A labeled group of question rows. Rendered by the caller only when it has rows, so headers
 * never sit alone. */
function HistoryGroup({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
	return (
		<div className="w-full">
			<Text as="div" type="Body/S/Medium" color={themeCSSVars.palette_N400} className="mb-1 px-2">
				{label}
			</Text>
			<div>{children}</div>
		</div>
	);
}

export function SphereAIHistorySection({
	chatHistory,
	loading,
	activeConversationUuid,
	onOpenConversation,
}: {
	chatHistory: ConversationInfo[] | undefined;
	/** The stored conversations are still being read — show placeholders, never the empty state. */
	loading?: boolean;
	/** The conversation currently open in the Chat tab (reopened from here), marked as active in
	 * the list; `null` while the Chat tab holds a fresh chat. */
	activeConversationUuid?: string | null;
	/** Reopen a stored conversation, restored as it was left (switches to Chat). */
	onOpenConversation: (conversationUuid: string) => void;
}): React.JSX.Element {
	const { t } = useTranslation();
	const [showAllRecent, setShowAllRecent] = useState(false);

	const deleteFn = async (conversationUuid?: string) => {
		try {
			if (!conversationUuid) {
				throw new Error("cannot delete conversationUuid of undefined");
			}
			// The repository invalidates the list itself — the row disappears without this component
			// knowing who owns the query.
			await sphereAiConversationsRepository.deleteConversation(conversationUuid);
		} catch (error) {
			reportPlatformError(error, "ERROR", "ask-intelligence");
		}
	};

	const renameFn = async (name: string, conversationUuid?: string) => {
		try {
			if (!conversationUuid) {
				throw new Error("cannot rename conversationUuid of undefined");
			}
			await sphereAiConversationsRepository.renameConversation(conversationUuid, name);
		} catch (error) {
			reportPlatformError(error, "ERROR", "ask-intelligence");
		}
	};

	const setConversationAsFavouriteFn = async (isFavourite: boolean, conversationUuid?: string) => {
		try {
			if (!conversationUuid) {
				throw new Error("cannot favourite conversationUuid of undefined");
			}
			await sphereAiConversationsRepository.setConversationFavourite(conversationUuid, isFavourite);
		} catch (error) {
			reportPlatformError(error, "ERROR", "ask-intelligence");
		}
	};

	const { favourites, recent } = useMemo(() => groupHistory(chatHistory ?? []), [chatHistory]);
	const visibleRecent = showAllRecent ? recent : recent.slice(0, RECENT_LIMIT);
	const hasMoreRecent = recent.length > RECENT_LIMIT;

	const isEmpty = favourites.length === 0 && recent.length === 0;

	const renderRow = (q: ConversationInfo, i: number) => (
		<HistoryBox
			key={q.uuid ?? i}
			message={q.name ?? ""}
			favourite={q.favourite}
			active={q.uuid != null && q.uuid === activeConversationUuid}
			onToggleFavourite={async () => {
				await setConversationAsFavouriteFn(!q.favourite, q.uuid ?? "");
			}}
			onOpen={() => {
				if (q.uuid) {
					onOpenConversation(q.uuid);
				}
			}}
			onDelete={() => deleteFn(q.uuid)}
			onRename={(newName) => renameFn(newName, q.uuid)}
		/>
	);

	return (
		<div className="flex h-full w-full flex-col bg-white">
			<ScrollWrapper className="flex-1">
				{/* All three states share the panel's lane, so nothing shifts as the read resolves and
				    nothing shifts when you come here from the Chat tab. It is what keeps each row's
				    favourite/menu controls within reach of the question they belong to in full screen —
				    see SphereAIPanelLane. */}
				<SphereAIPanelLane>
					{loading ? (
						<SphereAIHistoryLoading />
					) : isEmpty ? (
						<SphereAIHistoryEmpty />
					) : (
						/* The list is inset 8px so the rows' own `px-2` lands their text at the panel's 16px
						   gutter — the same lane as the header title, the tab labels and the Chat composer.
						   The remaining 8px is the row hover-highlight's bleed outside the text. */
						<div className="space-y-6 px-2 pt-4 pb-4">
							{favourites.length > 0 && (
								<HistoryGroup label={t("sphereAi.history.favourites")}>{favourites.map(renderRow)}</HistoryGroup>
							)}
							{recent.length > 0 && (
								<HistoryGroup label={t("sphereAi.history.recent")}>
									{visibleRecent.map(renderRow)}
									{hasMoreRecent && !showAllRecent && (
										<button
											type="button"
											onClick={() => setShowAllRecent(true)}
											className="flex w-full px-2 py-2 text-left transition-colors hover:bg-[var(--palette-N100)]"
										>
											<Text as="span" type="Body/S/Medium" color={themeCSSVars.palette_B500}>
												{t("sphereAi.history.showAll")}
											</Text>
										</button>
									)}
								</HistoryGroup>
							)}
						</div>
					)}
				</SphereAIPanelLane>
			</ScrollWrapper>
		</div>
	);
}
